import {
    collection,
    doc,
    addDoc,
    updateDoc,
    deleteDoc,
    getDocs,
    query,
    where,
    orderBy,
    onSnapshot,
    Timestamp,
    Unsubscribe,
    writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

export interface GeneratedImage {
    id: string;
    jobId: string;
    productId: string;
    productTitle: string;
    imageUrl: string; // URL from Firebase Storage
    storagePath?: string;
    mimeType: string;
    status: 'pending-review' | 'approved' | 'rejected' | 'uploaded' | 'failed';
    uploadedToShopify?: boolean;
    shopifyImageId?: string;
    error?: string;
    retryCount: number;
    usedPrompt?: string; // The exact prompt sent to AI
    seoFilename?: string; // SEO-friendly filename (without extension)
    altText?: string; // Alt text for accessibility and SEO
    createdAt: Date;
}

// Get all images for a job
export async function getJobImages(jobId: string): Promise<GeneratedImage[]> {
    const q = query(
        collection(db, 'generatedImages'),
        where('jobId', '==', jobId),
        orderBy('createdAt', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
    } as GeneratedImage));
}

// Subscribe to real-time updates for job images
export function subscribeToJobImages(jobId: string, callback: (images: GeneratedImage[]) => void): Unsubscribe {
    const q = query(
        collection(db, 'generatedImages'),
        where('jobId', '==', jobId),
        orderBy('createdAt', 'asc')
    );

    return onSnapshot(q, (snapshot) => {
        const images = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
        } as GeneratedImage));
        callback(images);
    }, (error) => {
        console.error('[subscribeToJobImages] Firestore error:', error);
        callback([]);
    });
}

// Get images pending review for a job
export async function getPendingReviewImages(jobId: string): Promise<GeneratedImage[]> {
    const q = query(
        collection(db, 'generatedImages'),
        where('jobId', '==', jobId),
        where('status', '==', 'pending-review')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
    } as GeneratedImage));
}

// Approve an image (mark for upload)
export async function approveImage(imageId: string): Promise<void> {
    const docRef = doc(db, 'generatedImages', imageId);
    await updateDoc(docRef, {
        status: 'approved',
    });
}

// Reject an image
export async function rejectImage(imageId: string): Promise<void> {
    const docRef = doc(db, 'generatedImages', imageId);
    await updateDoc(docRef, {
        status: 'rejected',
    });
}

// Update SEO metadata for an image
export async function updateImageSeoMetadata(
    imageId: string,
    seoData: { seoFilename?: string; altText?: string }
): Promise<void> {
    const docRef = doc(db, 'generatedImages', imageId);
    const updateData: Record<string, string> = {};

    if (seoData.seoFilename !== undefined) {
        updateData.seoFilename = seoData.seoFilename;
    }
    if (seoData.altText !== undefined) {
        updateData.altText = seoData.altText;
    }

    if (Object.keys(updateData).length > 0) {
        await updateDoc(docRef, updateData);
    }
}

// Bulk approve images
export async function bulkApproveImages(imageIds: string[]): Promise<void> {
    const batch = writeBatch(db);

    for (const imageId of imageIds) {
        const docRef = doc(db, 'generatedImages', imageId);
        batch.update(docRef, { status: 'approved' });
    }

    await batch.commit();
}

// Bulk reject images
export async function bulkRejectImages(imageIds: string[]): Promise<void> {
    const batch = writeBatch(db);

    for (const imageId of imageIds) {
        const docRef = doc(db, 'generatedImages', imageId);
        batch.update(docRef, { status: 'rejected' });
    }

    await batch.commit();
}

// Reset an image to pending-review
export async function resetImage(imageId: string): Promise<void> {
    const docRef = doc(db, 'generatedImages', imageId);
    await updateDoc(docRef, {
        status: 'pending-review',
    });
}

// Bulk reset images to pending-review
export async function bulkResetImages(imageIds: string[]): Promise<void> {
    const batch = writeBatch(db);

    for (const imageId of imageIds) {
        const docRef = doc(db, 'generatedImages', imageId);
        batch.update(docRef, { status: 'pending-review' });
    }

    await batch.commit();
}

// Delete an image
export async function deleteGeneratedImage(imageId: string): Promise<void> {
    const docRef = doc(db, 'generatedImages', imageId);
    await deleteDoc(docRef);
}

// Delete all images for a job
export async function deleteJobImages(jobId: string): Promise<void> {
    const images = await getJobImages(jobId);
    const batch = writeBatch(db);

    for (const image of images) {
        const docRef = doc(db, 'generatedImages', image.id);
        batch.delete(docRef);
    }

    await batch.commit();
}

// Add a generated image (used by Cloud Function, but also available client-side for testing)
export async function addGeneratedImage(data: Omit<GeneratedImage, 'id' | 'createdAt'>): Promise<string> {
    const docRef = await addDoc(collection(db, 'generatedImages'), {
        ...data,
        createdAt: Timestamp.now(),
    });
    return docRef.id;
}

// Mark image as uploaded to Shopify
export async function markImageAsUploaded(imageId: string, shopifyImageId: string): Promise<void> {
    const docRef = doc(db, 'generatedImages', imageId);
    await updateDoc(docRef, {
        status: 'uploaded',
        uploadedToShopify: true,
        shopifyImageId,
    });
}
